import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { NAV_LINKS } from "@/data/site";
import { fetchTestimonials } from "@/lib/testimonials";
import { scrollToId, scrollToTop } from "@/lib/scroll";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [hasTestimonials, setHasTestimonials] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        fetchTestimonials().then((t) => setHasTestimonials(t.length > 0));
    }, []);

    const links = NAV_LINKS.filter((l) => l.id !== "depoimentos" || hasTestimonials);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const goAnchor = (id) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/", { state: { scrollTo: id } });
        } else {
            scrollToId(id);
        }
    };

    const goHome = () => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/");
        } else {
            scrollToTop();
        }
    };

    const goRoute = (to) => {
        setOpen(false);
        navigate(to);
    };

    return (
        <>
            <header
                className={`sticky top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "border-b border-[#E2DACB]/80 bg-sand/90 shadow-sm backdrop-blur-xl"
                        : "border-b border-white/10 bg-forest-deep/40 backdrop-blur-md"
                }`}
            >
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
                    <button type="button" onClick={goHome} aria-label="Ir para a página inicial" className="shrink-0" data-testid="nav-brand">
                        <Logo dark={scrolled} />
                    </button>

                    <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegação principal">
                        {links.map((link) =>
                            link.type === "route" ? (
                                <button
                                    key={link.testid}
                                    type="button"
                                    data-testid={link.testid}
                                    onClick={() => goRoute(link.to)}
                                    className={`text-[13px] font-medium tracking-wide transition-colors duration-200 hover:text-gold ${
                                        scrolled ? "text-forest-deep" : "text-white/90"
                                    }`}
                                >
                                    {link.label}
                                </button>
                            ) : (
                                <button
                                    key={link.testid}
                                    type="button"
                                    data-testid={link.testid}
                                    onClick={() => goAnchor(link.id)}
                                    className={`text-[13px] font-medium tracking-wide transition-colors duration-200 hover:text-gold ${
                                        scrolled ? "text-forest-deep" : "text-white/90"
                                    }`}
                                >
                                    {link.label}
                                </button>
                            ),
                        )}
                    </nav>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="nav-cta-quote"
                            onClick={() => goAnchor("cotacao-rapida")}
                            className="hidden items-center gap-2 rounded-full bg-forest-emerald px-5 py-2.5 text-[13px] font-semibold text-white shadow-lg shadow-forest-deep/25 transition-all duration-200 hover:-translate-y-px hover:bg-forest-dark active:scale-[0.98] sm:inline-flex"
                        >
                            Solicitar Cotação
                            <ArrowRight className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            data-testid="nav-mobile-toggle"
                            aria-label={open ? "Fechar menu" : "Abrir menu"}
                            onClick={() => setOpen((v) => !v)}
                            className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors lg:hidden ${
                                scrolled ? "text-forest-deep hover:bg-forest-deep/5" : "text-white hover:bg-white/10"
                            }`}
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                <AnimatePresence>
                    {open && (
                        <motion.nav
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="overflow-hidden border-t border-[#E5DEC9] bg-sand/95 backdrop-blur-xl lg:hidden"
                            aria-label="Menu móvel"
                        >
                            <div className="flex flex-col gap-1 px-4 py-4">
                                {links.map((link) => (
                                    <button
                                        key={link.testid}
                                        type="button"
                                        data-testid={`mobile-${link.testid}`}
                                        onClick={() => (link.type === "route" ? goRoute(link.to) : goAnchor(link.id))}
                                        className="rounded-lg px-3 py-3 text-left text-sm font-medium text-forest-deep transition-colors hover:bg-forest-deep/5"
                                    >
                                        {link.label}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    data-testid="mobile-nav-cta-quote"
                                    onClick={() => goAnchor("cotacao-rapida")}
                                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-forest-emerald px-5 py-3 text-sm font-semibold text-white"
                                >
                                    Solicitar Cotação
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </motion.nav>
                    )}
                </AnimatePresence>
            </header>
        </>
    );
}
